import { useState, useEffect } from 'react'
import images from '@assets/images'
import './KeyGuard.css'

const SESSION_KEY = 'fenica_access'
// Bundle chỉ chứa HASH của key — key gốc không bao giờ xuất hiện trong source
const SECRET_KEY_HASH = import.meta.env.VITE_SECRET_KEY_HASH

/**
 * Hash một chuỗi bằng SHA-256 (Web Crypto API — có sẵn trên mọi trình duyệt modern)
 * @param {string} message
 * @returns {Promise<string>} hex string
 */
async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(message)
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Kiểm tra quyền truy cập:
 * 1. Nếu sessionStorage đã granted → cho vào ngay
 * 2. Nếu có ?key trên URL → hash nó → so với SECRET_KEY_HASH
 * @returns {Promise<boolean>}
 */
async function checkAccess() {
  if (sessionStorage.getItem(SESSION_KEY) === 'granted') return true

  const params = new URLSearchParams(window.location.search)
  const urlKey = params.get('key')
  if (!urlKey) return false

  const urlKeyHash = await sha256(urlKey)
  if (urlKeyHash === SECRET_KEY_HASH) {
    sessionStorage.setItem(SESSION_KEY, 'granted')
    return true
  }

  return false
}

function AccessDeniedScreen() {
  return (
    <main className="access-denied">
      {/* Background layers */}
      <div className="access-denied__grid" aria-hidden="true" />
      <div className="access-denied__glow access-denied__glow--1" aria-hidden="true" />
      <div className="access-denied__glow access-denied__glow--2" aria-hidden="true" />

      {/* Logo */}
      <div className="access-denied__logo">
        <img src={images.logo} alt="Fenica" />
      </div>

      <div className="access-denied__container">
        {/* Lock icon (CSS-only) */}
        <div className="access-denied__lock" aria-hidden="true">
          <div className="access-denied__lock-shackle" />
          <div className="access-denied__lock-body" />
        </div>

        <span className="access-denied__label">RESTRICTED</span>
        <div className="access-denied__code">403</div>

        <h1 className="access-denied__title">QUYỀN TRUY CẬP BỊ TỪ CHỐI</h1>
        <p className="access-denied__subtitle">ACCESS RESTRICTED</p>

        <div className="access-denied__divider">
          <span className="access-denied__divider-line" />
          <span className="access-denied__divider-dot" />
          <span className="access-denied__divider-line" />
        </div>

        <p className="access-denied__desc">
          Trang web này yêu cầu mã truy cập hợp lệ.<br />
          Vui lòng liên hệ quản trị viên để được cung cấp đường dẫn đúng.
        </p>
      </div>

      <footer className="access-denied__footer">
        © {new Date().getFullYear()} FENICA. All rights reserved.
      </footer>
    </main>
  )
}

export default function KeyGuard({ children }) {
  // null = đang kiểm tra, true = granted, false = denied
  const [granted, setGranted] = useState(null)

  useEffect(() => {
    checkAccess().then(setGranted)
  }, [])

  // Vẫn đang hash (thường < 1ms, chỉ là async)
  if (granted === null) return null

  if (!granted) return <AccessDeniedScreen />

  return children
}
